import * as React from 'react';
import { MobileTabBar, Toaster } from '@relay-towing/ui';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMember } from '../../hooks/useMember';
import { useCheckNewAssignments } from '../../hooks/useNewAssignments';
import { mobileTabsFor } from '../../lib/driverAppTabs';
import { isDismissed } from '../../lib/dismissals';
import { onNative } from '../../lib/nativeBridge';
import { WELCOME_ID, WelcomeScreen } from '../setup/WelcomeScreen';
import { AssignedCallWatcher } from './AssignedCallWatcher';
import { PresenceMenu } from './PresenceMenu';
import { SetupWatcher } from './SetupWatcher';
import { ShellContext } from './ShellContext';
import { TAB_DETAILS, tabForPath } from './tabs';

/**
 * The frame around every screen: the tab bar on the tab screens and the
 * lists opened from Functions or Operations, the presence-pill menu, the
 * prompt for a call just assigned, setup for a new owner, and the
 * wrapper's push taps routed into the app. Before any of it, once on a
 * device, the Welcome. Screens are routes, so a push, a link and the back
 * gesture all land on the same screen the same way; the old shell kept
 * them in component state.
 */
export function MobileShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const member = useMember();
  const checkAssigned = useCheckNewAssignments();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [welcomed, setWelcomed] = React.useState(() => isDismissed(WELCOME_ID));
  const shell = React.useMemo(
    () => ({ openMenu: () => setMenuOpen(true) }),
    []
  );

  // A push tapped while the app is open (docs/native-bridge.md). The
  // wrapper says nothing of a push until it is tapped, so the tap is also
  // when the assigned-call prompt hears of one. Listened for here, not in
  // the watcher: the first listener takes the taps that came before it.
  React.useEffect(
    () =>
      onNative('navigate', ({ path }) => {
        navigate(path);
        checkAssigned();
      }),
    [navigate, checkAssigned]
  );

  const tabIds = mobileTabsFor(member);
  const current = tabForPath(location.pathname, tabIds);
  const tabs = tabIds.map((id) => ({
    id,
    label: TAB_DETAILS[id].label,
    icon: TAB_DETAILS[id].icon,
  }));

  // The Figma's Welcome is a full page swap: until it is answered on this
  // device, it is the whole app. A push tapped meanwhile has already moved
  // the route, so the screen it opened is there once Welcome is done.
  if (!welcomed) return <WelcomeScreen onDone={() => setWelcomed(true)} />;

  return (
    <ShellContext.Provider value={shell}>
      <Outlet />
      {current ? (
        <MobileTabBar
          tabs={tabs}
          value={current}
          onValueChange={(id) =>
            navigate(TAB_DETAILS[id as keyof typeof TAB_DETAILS].path)
          }
        />
      ) : null}
      <PresenceMenu open={menuOpen} onOpenChange={setMenuOpen} />
      <AssignedCallWatcher />
      <SetupWatcher />
      <Toaster placement="phone" />
    </ShellContext.Provider>
  );
}
